import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { useFilterSet } from 'hooks/useFilterSet';
import { useFormContext } from 'react-hook-form';
import { useState, useMemo, useEffect, useCallback } from 'react';
import type { ReactElement } from 'react';
import type { TransitionProps } from '@mui/material/transitions';
import { createFilterSetMatcher, FilterSetMatcher } from 'utils/filterSetMatcher';
import { useFilterSetsSessionStore } from 'hooks/state/usePersistedStore';

import { MergeDialog } from './modals/MergeDialog';
import { DIALOG_STEPS, DialogStep } from './modals/constants';
import { FilterDialogProps, FilterItem } from './types';
import { RenderFilterItem } from './parts/FilterRenderer';
import { FilterSets } from './parts/FilterSets';
import { Module, MODULE_FILTER_CONFIG } from './moduleConfig';
import { AdvancedFilterFormControllerProps, AdvancedFilterData } from './FilterFormController';
import { useFilterFormMethods } from './hooks/useFilterFormMethods';
import { trackMatomoEvent } from 'services/matomo.service';
import { TbIcon } from 'components/Figma';
import { Button } from 'components/Button';

export interface AdvancedFilterDialogProps extends FilterDialogProps, AdvancedFilterFormControllerProps {
  module: Module;
  setFilterSetToDelete?: (id: string | null) => void;
  setFilterSetToRename?: (id: string | null) => void;
  currentDialog?: DialogStep;
}

function NoTransition({ children }: TransitionProps & { children: ReactElement }) {
  return children;
}

export function FilterDialog({
  open,
  onClose,
  module,
  setDialog,
  setFilterSetToDelete = () => {},
  setFilterSetToRename = () => {},
  currentDialog = DIALOG_STEPS.FILTER_SELECT,
}: AdvancedFilterDialogProps) {
  const intl = useIntl();
  const { filterSets } = useFilterSet();
  const formContext = useFormContext<AdvancedFilterData>();
  const { reset } = formContext;
  const { lastClickedFilterSet } = useFilterSetsSessionStore();
  const { resetToAppStore, resetToDefaults, applyFilters } = useFilterFormMethods();
  const [filterSetToMerge, setFilterSetToMerge] = useState<string | null>(null);

  const formValues = formContext.watch();
  const allFiltersAtDefaults = useMemo(() => FilterSetMatcher.areFiltersAtDefaults(formValues), [formValues]);

  const hasActiveFilterSet = useMemo(() => {
    const matcher = createFilterSetMatcher(filterSets, formValues.formSelectedFilterSet ?? null);
    return matcher.findMatchingFilterSet(formValues) !== null;
  }, [filterSets, formValues]);

  const pendingFilterSetName = useMemo(() => {
    if (!filterSetToMerge) return '';
    const filterSet = filterSets.find(set => set.filterSetId === filterSetToMerge);
    return filterSet?.name ?? '';
  }, [filterSetToMerge, filterSets]);

  useEffect(() => {
    if (open && currentDialog === DIALOG_STEPS.FILTER_SELECT) {
      trackMatomoEvent({ category: 'SaveFilters', action: 'ModalOpened', name: 'AllFiltersDialog' });
    }
  }, [open, currentDialog]);

  const handleClose = (_event: object, reason: string) => {
    if (reason === 'backdropClick') return;
    onClose();
  };

  const handleCancel = () => {
    resetToAppStore();
    formContext.setValue('formLastClickedFilterSet', lastClickedFilterSet);
    onClose();
  };

  const handleResetFilters = () => {
    resetToDefaults();
  };

  const handleMergeDialogCancel = () => {
    resetToAppStore();
    formContext.setValue('formLastClickedFilterSet', lastClickedFilterSet);
    setFilterSetToMerge(null);
    setDialog(DIALOG_STEPS.FILTER_SELECT);
  };

  const handleMergeDialogReplace = () => {
    if (!filterSetToMerge) return;

    const matcher = createFilterSetMatcher(filterSets, formValues.formSelectedFilterSet ?? null);
    const replaceValues = matcher.applyFilterSet(filterSetToMerge, formValues, 'replace');
    reset({
      ...replaceValues,
      formSelectedFilterSet: filterSetToMerge,
      formLastClickedFilterSet: null,
    });

    setFilterSetToMerge(null);
    setDialog(DIALOG_STEPS.FILTER_SELECT);
  };

  const handleMergeDialogMerge = () => {
    if (!filterSetToMerge) return;

    const matcher = createFilterSetMatcher(filterSets, formValues.formSelectedFilterSet ?? null);
    const mergedValues = matcher.applyFilterSet(filterSetToMerge, formValues, 'merge');
    reset({
      ...mergedValues,
      formSelectedFilterSet: filterSetToMerge,
      formLastClickedFilterSet: null,
    });

    setFilterSetToMerge(null);
    setDialog(DIALOG_STEPS.FILTER_SELECT);
  };

  const handleSaveFilterSetAsClick = () => {
    trackMatomoEvent({ category: 'SaveFilters', action: 'Click', name: 'SaveFilterSetAs' });
    setDialog('filterSave');
  };

  const renderSection = (section: FilterItem[], index: number) => (
    <div key={`section-${index}`}>
      {index !== 0 && <Divider light className="pt-3" />}
      <div className="grid grid-cols-2 gap-2 pt-6">
        {section.map((item, itemIndex) => RenderFilterItem(item, itemIndex))}
      </div>
      <div className="pb-3" />
    </div>
  );

  const filterSections = MODULE_FILTER_CONFIG[module] ?? {};
  const availableFilters = filterSections.available ?? [];
  const otherFilters = filterSections.other ?? [];
  const isMaxFilterSetsReached = filterSets.length >= 10;

  const updateSelectedFilterSet = useCallback(() => {
    const matcher = createFilterSetMatcher(filterSets, formValues.formSelectedFilterSet ?? null);
    const matchingSet = matcher.findMatchingFilterSet(formValues);
    const matchingSetId = matchingSet?.filterSetId ?? null;

    if (formValues.formSelectedFilterSet !== matchingSetId) {
      if (formValues.formSelectedFilterSet) {
        formContext.setValue('formLastClickedFilterSet', formValues.formSelectedFilterSet);
      }
      formContext.setValue('formSelectedFilterSet', matchingSetId);
    }
  }, [filterSets, formValues, formContext]);

  useEffect(() => {
    if (!open) return undefined;

    const timeoutId = setTimeout(updateSelectedFilterSet, 100);
    return () => clearTimeout(timeoutId);
  }, [open, updateSelectedFilterSet]);

  return (
    <>
      {open && currentDialog !== DIALOG_STEPS.FILTER_MERGE && (
        <Dialog
          open
          onClose={handleClose}
          TransitionComponent={NoTransition}
          slotProps={{ backdrop: { TransitionComponent: NoTransition } }}
          fullWidth
          maxWidth="md"
          className="advanced-filter-dialog"
          disableRestoreFocus
          aria-labelledby="advanced-filter-dialog-title"
          aria-describedby="advanced-filter-dialog-description"
        >
          <DialogTitle id="advanced-filter-dialog-title">
            <FormattedMessage id="advanced-filter-dialog-title" defaultMessage="All Filters" />
            <Button icon="Cross" aria-label="close" onClick={handleCancel} className="ml-auto" tabIndex={0} />
          </DialogTitle>
          <DialogContent id="advanced-filter-dialog-description" className="advanced-filter-dialog-content">
            <FilterSets
              setDialog={setDialog}
              setFilterSetToDelete={setFilterSetToDelete}
              setFilterSetToRename={setFilterSetToRename}
              setFilterSetToMerge={setFilterSetToMerge}
            />
            <div>
              <div className="mb-6">
                <div className="flex justify-between items-center h-8">
                  <p className="text-md font-medium pb-3">
                    <FormattedMessage id="advanced-filter-available" defaultMessage="Available filters " />
                    <FormattedMessage id={`${module}`} defaultMessage={module} />
                  </p>
                  {!allFiltersAtDefaults && (
                    <Button priority="ghost" iconLeft="Cross" size="sm" onClick={handleResetFilters}>
                      <FormattedMessage id="general.button.clear.all" defaultMessage="Clear all" />
                    </Button>
                  )}
                </div>
                <Divider />
                {availableFilters.map((section, index) => renderSection(section, index))}
              </div>
              {otherFilters.length > 0 && (
                <div className="mb-6">
                  <Accordion>
                    <AccordionSummary
                      expandIcon={<TbIcon name="ChevronDown" size="lg" id="advanced-filter-accordion-expand-trigger" />}
                    >
                      <Typography>
                        {intl.formatMessage({ id: 'advanced-filter-others', defaultMessage: 'Other filters' })}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      {otherFilters.map((section, index) => renderSection(section, index))}
                    </AccordionDetails>
                  </Accordion>
                </div>
              )}
              <Button
                id="advanced-filter-open-save-dialog-button"
                priority="secondary"
                onClick={handleSaveFilterSetAsClick}
                disabled={isMaxFilterSetsReached || hasActiveFilterSet || allFiltersAtDefaults}
                iconLeft="HeartOutlined"
              >
                <FormattedMessage id="advanced-filter-set-save-as" />
              </Button>
            </div>
          </DialogContent>

          <DialogActions>
            <Button id="advanced-filter-dialog-cancel-button" priority="secondary" onClick={handleCancel}>
              <FormattedMessage id="general.button.cancel" defaultMessage="Cancel" />
            </Button>
            <Button id="advanced-filter-dialog-apply-button" onClick={applyFilters}>
              <FormattedMessage id="general.button.apply" defaultMessage="Apply" />
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <MergeDialog
        open={currentDialog === DIALOG_STEPS.FILTER_MERGE}
        onClose={() => setDialog(DIALOG_STEPS.FILTER_SELECT)}
        onCancel={handleMergeDialogCancel}
        onReplace={handleMergeDialogReplace}
        onMerge={handleMergeDialogMerge}
        filterSetName={pendingFilterSetName}
      />
    </>
  );
}
